import type { SchemaFailureHandler } from "@reduxjs/toolkit/query";
import * as validator from "validator";
import { z } from "zod";

import type { CustomBaseQueryError } from "api/types/error";
import { logger } from "common/utilities/logger";
import { unsafelyEntries } from "common/utilities/object";

export const handleSchemaFailure: SchemaFailureHandler = (error, info) => {
  const errorMessage = `Schema validation error at endpoint: ${info.endpoint}\n${JSON.stringify(error.issues)}`;
  logger.error(errorMessage, error);
};

export const toSearchString = (params: Record<string, string | number | boolean | undefined>) => {
  const searchParams = new URLSearchParams();
  unsafelyEntries(params).forEach(([key, value]) => {
    if (value !== undefined) searchParams.set(key, value.toString());
  });
  const searchParamsString = searchParams.toString();
  return searchParamsString.length <= 0 ? searchParamsString : `?${searchParamsString}`;
};

// Return type generated from intelligence-native typing
export const nullToUndefined = <T extends z.ZodType>(
  schema: T,
): z.ZodEffects<
  z.ZodOptional<z.ZodNullable<T>>,
  NonNullable<T["_output"]> | undefined,
  T["_input"] | null | undefined
> => schema.nullish().transform((value: T | null | undefined) => value ?? undefined);

export const isCustomBaseQueryError = (err: unknown): err is CustomBaseQueryError =>
  typeof err === "object" && err !== null && "status" in err && "data" in err;

export const validHttpUrl = (): z.ZodEffects<z.ZodString, string, string> =>
  z
    .string()
    .url()
    .refine(
      (url) =>
        validator.isURL(url, {
          protocols: ["http", "https"],
          require_protocol: true,
          allow_underscores: false,
          allow_trailing_dot: false,
          require_tld: true,
        }),
      { message: "URL must be a valid HTTP/HTTPS URL" },
    );
